/**
 * CardCaptureModal - In-app credit card capture using Stripe Elements
 * 
 * PURPOSE: Collect and validate payment methods without leaving the app.
 * 
 * FLOW:
 * 1. Create Setup Intent on backend
 * 2. Collect card details using Stripe Elements
 * 3. Confirm Setup Intent to vault the card
 * 4. Attach payment method to billing profile
 * 5. Upgrade user to A La Carte if needed
 * 
 * NO REDIRECTS - Everything happens in-app.
 */

import React, { useState, useEffect } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import {
  Elements,
  CardElement,
  useStripe,
  useElements
} from '@stripe/react-stripe-js';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Button } from '@/components/ui/button';
import { CreditCard, Lock, Check, Loader2, AlertCircle } from 'lucide-react';
import { useNotification } from '@/contexts/NotificationContext';
import { createSetupIntent, attachPaymentMethod, getBillingProfileStatus } from '@/services/billingService';
import { API_URL } from '@/config/api';

// API_URL imported from centralized config

// Stripe promise - will be initialized with publishable key from backend
let stripePromise = null;

// Mock Card Form - Used when Stripe is in test/mock mode
const MockCardForm = ({ 
  userId, 
  token, 
  onSuccess, 
  onCancel,
  membershipType,
  setupIntentData,
  onError,
  onBillingSuccess
}) => {
  const [processing, setProcessing] = useState(false);
  const [cardNumber, setCardNumber] = useState('');
  const [cardExpiry, setCardExpiry] = useState('');
  const [cardCvc, setCardCvc] = useState('');
  const [cardComplete, setCardComplete] = useState(false);

  // Check if card details are complete
  useEffect(() => {
    const isComplete = cardNumber.length >= 15 && cardExpiry.length >= 4 && cardCvc.length >= 3;
    setCardComplete(isComplete);
  }, [cardNumber, cardExpiry, cardCvc]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!cardComplete) {
      onError('Please enter valid card details');
      return;
    }

    setProcessing(true);

    try {
      console.log('[CARD] Mock mode - simulating card setup');
      
      // In mock mode, directly call attach payment method with mock data
      const result = await attachPaymentMethod(
        userId,
        `pm_mock_${Date.now()}`,
        true, // accept billing agreement
        token
      );

      onBillingSuccess('Payment method added successfully!');
      onSuccess(result);
    } catch (error) {
      console.error('[CARD] Mock error:', error);
      onError(error.message || 'Failed to add payment method');
    } finally {
      setProcessing(false);
    }
  };

  const showUpgradeNotice = membershipType === 'Free' || membershipType === 'Introductory';

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Upgrade Notice */}
      {showUpgradeNotice && (
        <div className="bg-amber-900/30 border border-amber-700 rounded-lg p-4 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
          <div>
            <div className="text-amber-300 font-medium">Upgrade Notice</div>
            <div className="text-amber-400/80 text-sm">
              Adding a payment method will upgrade your account to <strong>A La Carte</strong> status, 
              giving you access to purchase credits at our standard rates.
            </div>
          </div>
        </div>
      )}

      {/* Security Benefits */}
      <div className="bg-slate-700/50 rounded-lg p-4 space-y-2">
        <div className="flex items-center gap-2 text-green-400">
          <Check className="w-4 h-4" />
          <span className="text-sm">Your card details are securely stored by Stripe</span>
        </div>
        <div className="flex items-center gap-2 text-green-400">
          <Check className="w-4 h-4" />
          <span className="text-sm">Card will only be charged when you make a purchase</span>
        </div>
        <div className="flex items-center gap-2 text-green-400">
          <Check className="w-4 h-4" />
          <span className="text-sm">You can update or remove your card at any time</span>
        </div>
      </div>

      {/* Mock Card Input */}
      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-slate-300 mb-2">
            <Lock className="inline w-4 h-4 mr-2 text-green-400" />
            Card Number
          </label>
          <input
            type="text"
            placeholder="4242 4242 4242 4242"
            value={cardNumber}
            onChange={(e) => setCardNumber(e.target.value.replace(/\D/g, '').slice(0, 16))}
            className="w-full p-3 bg-slate-700/50 rounded-lg border border-slate-600 text-slate-100 placeholder-slate-500 focus:border-amber-500 focus:outline-none"
          />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">Expiry</label>
            <input
              type="text"
              placeholder="MM/YY"
              value={cardExpiry}
              onChange={(e) => setCardExpiry(e.target.value.replace(/\D/g, '').slice(0, 4))}
              className="w-full p-3 bg-slate-700/50 rounded-lg border border-slate-600 text-slate-100 placeholder-slate-500 focus:border-amber-500 focus:outline-none"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">CVC</label>
            <input
              type="text"
              placeholder="123"
              value={cardCvc}
              onChange={(e) => setCardCvc(e.target.value.replace(/\D/g, '').slice(0, 4))}
              className="w-full p-3 bg-slate-700/50 rounded-lg border border-slate-600 text-slate-100 placeholder-slate-500 focus:border-amber-500 focus:outline-none"
            />
          </div>
        </div>
        <p className="text-xs text-amber-400 text-center">
          🔧 Test Mode: Use any card number (e.g., 4242424242424242)
        </p>
      </div>

      {/* Actions */}
      <div className="flex gap-3 pt-2">
        <Button
          type="button"
          variant="ghost"
          onClick={onCancel}
          disabled={processing}
          className="flex-1 text-slate-300"
        >
          Cancel
        </Button>
        <Button
          type="submit"
          disabled={processing || !cardComplete}
          className="flex-1 bg-amber-500 hover:bg-amber-600 text-slate-900"
          data-testid="submit-card-btn"
        >
          {processing ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Processing...
            </>
          ) : (
            <>
              <CreditCard className="w-4 h-4 mr-2" />
              Add Card
            </>
          )}
        </Button>
      </div>

      {/* Stripe Badge */}
      <div className="text-center pt-2">
        <p className="text-xs text-slate-500 flex items-center justify-center gap-1">
          <Lock className="w-3 h-3" />
          Secured by Stripe (Test Mode)
        </p>
      </div>
    </form>
  );
};

const CardForm = ({ 
  userId, 
  userEmail, 
  userName, 
  token, 
  onSuccess, 
  onCancel,
  membershipType,
  setupIntentData,
  onError,
  onBillingSuccess
}) => {
  const stripe = useStripe();
  const elements = useElements();
  const [processing, setProcessing] = useState(false);
  const [cardComplete, setCardComplete] = useState(false);
  const [cardError, setCardError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!stripe || !elements) {
      onError('Payment system not ready. Please try again.');
      return;
    }

    if (!cardComplete) {
      onError('Please enter valid card details');
      return;
    }

    setProcessing(true);
    setCardError(null);

    try {
      const cardElement = elements.getElement(CardElement);

      // Check if this is mock mode
      if (setupIntentData.is_mock) {
        console.log('[CARD] Mock mode - simulating card setup');
        
        // In mock mode, directly call attach payment method with mock data
        const result = await attachPaymentMethod(
          userId,
          `pm_mock_${Date.now()}`,
          true, // accept billing agreement
          token
        );

        onBillingSuccess('Payment method added successfully!');
        onSuccess(result);
        return;
      }

      // Real Stripe flow - Confirm the Setup Intent
      const { error, setupIntent } = await stripe.confirmCardSetup(
        setupIntentData.client_secret,
        {
          payment_method: {
            card: cardElement,
            billing_details: {
              name: userName,
              email: userEmail
            }
          }
        }
      );

      if (error) {
        console.error('[CARD] Setup failed:', error);
        setCardError(error.message);
        onError(error.message || 'Failed to add card');
        return;
      }

      if (setupIntent.status === 'succeeded') {
        console.log('[CARD] Setup succeeded, payment method:', setupIntent.payment_method);
        
        // Attach the payment method to the billing profile
        const result = await attachPaymentMethod(
          userId,
          setupIntent.payment_method,
          true, // accept billing agreement
          token
        );

        onBillingSuccess('Payment method added successfully!');
        onSuccess(result);
      } else {
        onError(`Setup incomplete: ${setupIntent.status}`);
      }
    } catch (error) {
      console.error('[CARD] Error:', error);
      setCardError(error.message || 'An error occurred');
      onError(error.message || 'Failed to add payment method');
    } finally {
      setProcessing(false);
    }
  };

  const cardElementOptions = {
    style: {
      base: {
        fontSize: '16px',
        color: '#e2e8f0',
        '::placeholder': {
          color: '#64748b',
        },
        iconColor: '#f59e0b',
      },
      invalid: {
        color: '#ef4444',
        iconColor: '#ef4444',
      },
    },
    hidePostalCode: false,
  };

  const showUpgradeNotice = membershipType === 'Free' || membershipType === 'Introductory';

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Upgrade Notice */}
      {showUpgradeNotice && (
        <div className="bg-amber-900/30 border border-amber-700 rounded-lg p-4 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
          <div>
            <div className="text-amber-300 font-medium">Upgrade Notice</div>
            <div className="text-amber-400/80 text-sm">
              Adding a payment method will upgrade your account to <strong>A La Carte</strong> status, 
              giving you access to purchase credits at our standard rates.
            </div>
          </div>
        </div>
      )}

      {/* Security Benefits */}
      <div className="bg-slate-700/50 rounded-lg p-4 space-y-2">
        <div className="flex items-center gap-2 text-green-400">
          <Check className="w-4 h-4" />
          <span className="text-sm">Your card details are securely stored by Stripe</span>
        </div>
        <div className="flex items-center gap-2 text-green-400">
          <Check className="w-4 h-4" />
          <span className="text-sm">Card will only be charged when you make a purchase</span>
        </div>
        <div className="flex items-center gap-2 text-green-400">
          <Check className="w-4 h-4" />
          <span className="text-sm">You can update or remove your card at any time</span>
        </div>
      </div>

      {/* Card Input */}
      <div className="space-y-2">
        <label className="block text-sm font-medium text-slate-300">
          <Lock className="inline w-4 h-4 mr-2 text-green-400" />
          Card Details
        </label>
        <div className="p-4 bg-slate-700/50 rounded-lg border border-slate-600 focus-within:border-amber-500 transition-colors">
          <CardElement
            options={cardElementOptions}
            onChange={(e) => {
              setCardComplete(e.complete);
              if (e.error) {
                setCardError(e.error.message);
              } else {
                setCardError(null);
              }
            }}
          />
        </div>
        {cardError && (
          <p className="text-sm text-red-400 flex items-center gap-1">
            <AlertCircle className="w-4 h-4" />
            {cardError}
          </p>
        )}
      </div>

      {/* Actions */}
      <div className="flex gap-3 pt-2">
        <Button
          type="button"
          variant="ghost"
          onClick={onCancel}
          disabled={processing}
          className="flex-1 text-slate-300"
        >
          Cancel
        </Button>
        <Button
          type="submit"
          disabled={!stripe || processing || !cardComplete}
          className="flex-1 bg-amber-500 hover:bg-amber-600 text-slate-900"
          data-testid="submit-card-btn"
        >
          {processing ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Processing...
            </>
          ) : (
            <>
              <CreditCard className="w-4 h-4 mr-2" />
              Add Card
            </>
          )}
        </Button>
      </div>

      {/* Stripe Badge */}
      <div className="text-center pt-2">
        <p className="text-xs text-slate-500 flex items-center justify-center gap-1">
          <Lock className="w-3 h-3" />
          Secured by Stripe
        </p>
      </div>
    </form>
  );
};

const CardCaptureModal = ({
  isOpen,
  onClose,
  onSuccess,
  userId,
  userEmail,
  userName,
  token,
  membershipType = 'Free'
}) => {
  const { notifyError, notifyBilling } = useNotification();
  const [loading, setLoading] = useState(true);
  const [setupIntentData, setSetupIntentData] = useState(null);
  const [error, setError] = useState(null);

  // Initialize Stripe and create Setup Intent when modal opens
  useEffect(() => {
    const initializePayment = async () => {
      if (!isOpen || !userId) return;
      
      setLoading(true);
      setError(null);

      try {
        // Create Setup Intent
        console.log('[CARD_MODAL] Creating Setup Intent for user:', userId);
        const setupData = await createSetupIntent(userId, userEmail, userName, token);
        console.log('[CARD_MODAL] Setup Intent created:', setupData);
        
        setSetupIntentData(setupData);

        // Initialize Stripe with the publishable key from backend
        if (setupData.publishable_key && !stripePromise) {
          console.log('[CARD_MODAL] Initializing Stripe with key:', setupData.publishable_key.substring(0, 10) + '...');
          stripePromise = loadStripe(setupData.publishable_key);
        }
      } catch (err) {
        console.error('[CARD_MODAL] Error:', err);
        setError(err.message || 'Failed to initialize payment form');
        notifyError('Failed to initialize payment form');
      } finally {
        setLoading(false);
      }
    };

    initializePayment();
  }, [isOpen, userId, userEmail, userName, token, notifyError]);

  const handleSuccess = async (result) => {
    console.log('[CARD_MODAL] Payment method added:', result);
    onSuccess?.(result);
    onClose();
  };

  const handleCancel = () => {
    onClose();
  };

  if (!isOpen) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="bg-slate-800 border-slate-700 max-w-md">
        {/* NewAudia Logo */}
        <div className="flex justify-center pt-2 pb-4">
          <img 
            src="/newaudia-logo.jpg" 
            alt="NewAudia™" 
            className="h-12 w-auto rounded"
          />
        </div>
        
        <DialogHeader>
          <DialogTitle className="text-white text-center flex items-center justify-center gap-2">
            <CreditCard className="w-5 h-5 text-amber-400" />
            Add Payment Method
          </DialogTitle>
          <DialogDescription className="text-slate-400 text-center">
            Securely add your credit card for purchases
          </DialogDescription>
        </DialogHeader>
        
        <div className="py-4">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-8">
              <Loader2 className="w-8 h-8 text-amber-500 animate-spin mb-3" />
              <p className="text-slate-400 text-sm">Initializing secure payment form...</p>
            </div>
          ) : error ? (
            <div className="text-center py-8">
              <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-3" />
              <p className="text-red-400 mb-4">{error}</p>
              <Button onClick={() => onClose()} className="bg-slate-700">
                Close
              </Button>
            </div>
          ) : setupIntentData ? (
            stripePromise ? (
              <Elements stripe={stripePromise}>
                <CardForm
                  userId={userId}
                  userEmail={userEmail}
                  userName={userName}
                  token={token}
                  onSuccess={handleSuccess}
                  onCancel={handleCancel}
                  membershipType={membershipType}
                  setupIntentData={setupIntentData}
                  onError={notifyError}
                  onBillingSuccess={notifyBilling}
                />
              </Elements>
            ) : (
              // Mock mode - render mock form without Stripe Elements
              <MockCardForm
                userId={userId}
                token={token}
                onSuccess={handleSuccess}
                onCancel={handleCancel}
                membershipType={membershipType}
                setupIntentData={setupIntentData}
                onError={notifyError}
                onBillingSuccess={notifyBilling}
              />
            )
          ) : error ? (
            <div className="text-center py-8">
              <div className="text-red-400 mb-4">
                <CreditCard className="w-12 h-12 mx-auto mb-2 opacity-50" />
                <p className="font-medium">Unable to initialize payment form</p>
              </div>
              <p className="text-slate-400 text-sm mb-4">{error}</p>
              <div className="flex justify-center gap-3">
                <Button 
                  variant="outline" 
                  onClick={onClose}
                  className="text-slate-300 border-slate-600"
                >
                  Cancel
                </Button>
                <Button 
                  onClick={() => {
                    setError(null);
                    setLoading(true);
                    // Re-trigger the useEffect by toggling
                  }}
                  className="bg-amber-500 hover:bg-amber-600 text-black"
                >
                  Try Again
                </Button>
              </div>
            </div>
          ) : (
            <div className="text-center py-8 text-slate-400">
              Unable to load payment form. Please try again later.
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default CardCaptureModal;
